import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap, catchError, from, switchMap, combineLatest, throwError, Observable, map, take, mergeMap, of, concatMap } from 'rxjs';
import { LoggerService } from './utils/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './utils/toast.service';
import { MenuController } from '@ionic/angular';
import { UserService } from './user.service';
import { UserCredentials } from '../../shared/models/user-credentials';
import { User } from '../../shared/models/user';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '../../shared/models/login-response';
import { IonicColors } from '../../shared/enums/ionicColors.enum';
import { RequestsService } from './utils/requests.service';
import { NotificationService } from './notification.service';
import { cleanLocalStorage } from '../../shared/utils/generic';
import { UsageTrackingService } from './usage-tracking.service';
import { ClientLogsService } from './client-logs.service';

@Injectable({
  providedIn: 'any'
})
export class AuthService {

  constructor(
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private menuController: MenuController,
    private _http: HttpClient,
    private userService: UserService,
    private requestsService: RequestsService,
    private notificationService: NotificationService,
    private usageTrackingService: UsageTrackingService,
    private clientLogs: ClientLogsService
  ) {}

  public getServiceStatus$(): Observable<boolean> {
    return this._http.get<{message: string}>(`${environment.apiEndpoint}/auth/ping`).pipe(
      take(1),
      map((val: {message: string}) => !!val.message),
      catchError(err => {
        this.loggerService.error('Error on .../ping');
        return of(false);
      })
    );
  }

  /**
   * @description Funzione che si occupa di fare la chiamata di login
   * @param userCredentials
   */
  public login(userCredentials: UserCredentials, isMobile?: boolean) {
    const clientContext = this.clientLogs.clientContext();
    return this._http.post<LoginResponse>(
      `${environment.apiEndpoint}/auth`,
      { ...userCredentials, clientContext },
      { withCredentials: true }
    ).pipe(
      take(1),
      tap((response: LoginResponse) => {
        localStorage.setItem('ACCESS_TOKEN', response.access_token || "");
        this.clientLogs.startSession(response.userId, response.logSessionId, clientContext, response.logCapabilities);
        this.initializeLanguage(navigator.language);
        this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.LOGGED_SUCCESFULLY'), IonicColors.GREEN);
        this.loggerService.debug('Login OK 🟢');
      }),
      switchMap((loginResponse) => this.userService.getUser$(String(loginResponse.userId))),
      tap((user) => {
        this.requestsService.saveCurrentUser(user);
        this.clientLogs.authenticationAvailable();
        this.translateService.use(user.language || "it");
        this.loggerService.debug('Get User OK 🟢');
      }),
      catchError(err => {
        cleanLocalStorage();
        this.requestsService.saveCurrentUser(null);
        !isMobile && this.router.navigate(['/wita']);
        throw err;
      })
    )
  }

  /**
   * @description Notifica al server la disconnessione dell'utente dal dispositivo e poi esegue il logout
   */
  logout$(){
    this.notificationService.removeToken();
    return from(this.clientLogs.prepareForLogout()).pipe(
      switchMap(() => from(this.usageTrackingService.prepareForLogout())),
      catchError((error) => {
        this.loggerService.warn('Unable to close usage sessions before logout', error);
        return of(undefined);
      }),
      switchMap(() => this._http.post<any>(
        `${environment.apiEndpoint}/auth/logout`, {}, { withCredentials: true }
      )),
        take(1),
        tap(() => {
          cleanLocalStorage();
          this.requestsService.saveCurrentUser(null);
          this.router.navigateByUrl('auth');
        }),
        catchError(error => {
          this.clientLogs.resumeAfterFailedLogout();
          return throwError(() => error);
        })
    )
  }

  public sendEmail$(email: {email: string, type: string}) {
    const headers = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    const options = { headers: headers };
    const url = `${environment.apiEndpoint}/auth/request`;
    return this._http.post(url, email, options).pipe(
      take(1),
      catchError((err) => {
        this.loggerService.error(err);
        throw err;
      })
    );
  }

  public sendForgotPwMail(email: {email: string}) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = { headers: headers };
    const url = `${environment.apiEndpoint}/users/forgot-password`;
    return this._http.post(url, email, options).pipe(
      take(1),
      catchError((err) => {
        this.loggerService.error(err);
        throw err;
      })
    );
  }

  public setPassword$(tokenAndPassword: {token: string, password: string}) {
    const headers = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    const options = { headers: headers };
    const url = `${environment.apiEndpoint}/users/activate`;
    return this._http.post(url, tokenAndPassword, options).pipe(
      take(1),
      catchError((err) => {
        this.loggerService.error(err);
        throw err;
      })
    );
  }

  public resetPassword$(tokenAndPassword: {token: string, password: string}) {
    const headers = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    const options = { headers: headers };
    const url = `${environment.apiEndpoint}/users/reset-password`;
    return this._http.post(url, tokenAndPassword, options).pipe(
      take(1),
      catchError((err) => {
        this.loggerService.error(err);
        throw err;
      })
    );
  }

  /**
   * @description Sulla base della presenza dell'ACCES_TOKEN sul localStorage torna true o false
   */
  public isLoggedIn(){
    return localStorage.getItem('ACCESS_TOKEN') !== null;
  }

  /**
   * @description effettua il logout dall'app senza notificare la disconnessione dal dispositivo al server (ad esempio nel caso di ricezione di un error unauthorized)
   */
  public appLogout(){
    this.usageTrackingService.discardAllSessions();
    cleanLocalStorage();
    this.requestsService.saveCurrentUser(null);
    this.menuController?.close();
    this.router.navigate(['/auth']);
  }

  /**
   * @description Imposto le traduzioni sulla base della lingua dello user o, se non esistente, del browser, di default metto 'it'
   * @param browserLanguage 
   */
  private initializeLanguage(browserLanguage: string) {
    if (['it', 'en', 'fr'].includes(this.requestsService.getCurrentUser()?.language || "")) {
      this.translateService.use(this.requestsService.getCurrentUser()!.language);
    } else {
        if (browserLanguage.includes('en-')) {
          this.translateService.use('en');
        } else if (browserLanguage.includes('fr-')) {
          this.translateService.use('fr');
        } else {
          this.translateService.use('it');
        }
    }
  }

  /**
   * @description Chiamo la refreshToken pescando il precedente dai cookies
   * @returns Torno l'observable della chiamata
   */
  public callRefreshToken$() {
    return this._http
      .post<any>(
        `${environment.apiEndpoint}/auth/refreshToken`,
        {},
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      )
      .pipe(
        take(1),
        tap((response) => {
          response.access_token && localStorage.setItem('ACCESS_TOKEN', response.access_token);
          this.clientLogs.authenticationAvailable();
        })
      );
  }
}
